/**
 * Tenant custom fields editor — the flank counterpart of flank-20's
 * "Custom fields" dialog (FLA2-639/640/644/648).
 *
 * Kept deliberately close to the 2.0 experience so partner-facing walkthroughs
 * describe one product: four tabs, formula validation shown inline as you type,
 * and first-match-wins dimension buckets with a named fallback.
 */
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { CANONICAL_METRICS, type MetricFormat } from '../lib/canonicalMetrics';
import {
  CUSTOM_FIELD_PREFIX,
  type CalculatedMetricDef,
  type CustomDimensionDef,
  type DimensionBucket,
  type TenantFields,
  validateFormula,
} from '../lib/tenantFields';

const FORMATS: { value: MetricFormat; label: string }[] = [
  { value: 'currency', label: 'Currency' },
  { value: 'count', label: 'Number' },
  { value: 'percent', label: 'Percent' },
  { value: 'ratio', label: 'Ratio' },
  { value: 'multiple', label: 'Multiple' },
];

const newKey = (prefix: string) => `${CUSTOM_FIELD_PREFIX}${prefix}-${Math.random().toString(36).slice(2, 9)}`;

interface Props {
  open: boolean;
  onClose: () => void;
  fields: TenantFields;
  onChange: (next: TenantFields) => void;
  /** Canonical dimensions a custom dimension may bucket: { value, label }. */
  dimensionOptions: { value: string; label: string }[];
  /** flank#5101: canonical metric keys this viewer is permitted to see — a
   * formula referencing, or a visibility toggle for, anything outside this
   * list (e.g. a client without Spend) must not be offered here, same as
   * the widget editor's own metric picker already excludes it. */
  allowedMetricKeys: string[];
}

const TenantFieldsDialog = ({ open, onClose, fields, onChange, dimensionOptions, allowedMetricKeys }: Props) => {
  const [tab, setTab] = useState(0);

  // Calculated metric draft
  const [mLabel, setMLabel] = useState('');
  const [mFormat, setMFormat] = useState<MetricFormat>('currency');
  const [mFormula, setMFormula] = useState('');
  const formulaCheck = useMemo(
    () => (mFormula.trim() ? validateFormula(mFormula, allowedMetricKeys) : null),
    [mFormula, allowedMetricKeys],
  );

  // Custom dimension draft
  const [dLabel, setDLabel] = useState('');
  const [dField, setDField] = useState(dimensionOptions[0]?.value ?? 'channel');
  const [dFallback, setDFallback] = useState('Other');
  const [dBuckets, setDBuckets] = useState<DimensionBucket[]>([{ label: '', values: [] }]);

  const addMetric = () => {
    if (!mLabel.trim() || !formulaCheck?.ok) return;
    const metric: CalculatedMetricDef = {
      key: newKey('metric'),
      label: mLabel.trim(),
      format: mFormat,
      formula: mFormula.trim(),
    };
    onChange({ ...fields, metrics: [...fields.metrics, metric] });
    setMLabel('');
    setMFormula('');
  };

  const addDimension = () => {
    const buckets = dBuckets.filter(b => b.label.trim() && b.values.length);
    if (!dLabel.trim() || !buckets.length) return;
    const dimension: CustomDimensionDef = {
      key: newKey('dim'),
      label: dLabel.trim(),
      field: dField,
      buckets,
      fallback: dFallback.trim() || 'Other',
    };
    onChange({ ...fields, dimensions: [...fields.dimensions, dimension] });
    setDLabel('');
    setDBuckets([{ label: '', values: [] }]);
  };

  const setBucket = (index: number, patch: Partial<DimensionBucket>) =>
    setDBuckets(prev => prev.map((b, i) => (i === index ? { ...b, ...patch } : b)));

  const removeBucket = (index: number) => setDBuckets(prev => prev.filter((_, i) => i !== index));

  // Bucket order decides the match, so a dimension with no usable bucket would
  // send every row to the fallback. Disable rather than fail silently on click.
  const canAddDimension = !!dLabel.trim() && dBuckets.some(b => b.label.trim() && b.values.length > 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle>Custom fields</DialogTitle>
      <DialogContent dividers>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant='scrollable'>
          <Tab label='Calculated metrics' />
          <Tab label='Custom dimensions' />
          <Tab label='Rename fields' />
          <Tab label='Metric visibility' />
        </Tabs>

        {tab === 0 && (
          <Box className='mt-4 flex flex-col gap-3'>
            <Box className='flex gap-2'>
              <TextField
                size='small'
                label='Name'
                placeholder='Cost per visit'
                value={mLabel}
                onChange={e => setMLabel(e.target.value)}
                fullWidth
              />
              <Select size='small' value={mFormat} onChange={e => setMFormat(e.target.value as MetricFormat)}>
                {FORMATS.map(f => (
                  <MenuItem key={f.value} value={f.value}>
                    {f.label}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <TextField
              size='small'
              label='Formula'
              placeholder='spend / store_visits'
              value={mFormula}
              onChange={e => setMFormula(e.target.value)}
              error={!!formulaCheck && !formulaCheck.ok}
              helperText={formulaCheck && !formulaCheck.ok ? formulaCheck.error : 'Arithmetic over canonical metrics.'}
              fullWidth
            />
            <details>
              <summary className='cursor-pointer text-[12px] text-gray-600'>Valid metric keys</summary>
              <Box className='mt-2 flex flex-wrap gap-1'>
                {allowedMetricKeys.map(k => (
                  <Chip key={k} label={k} size='small' variant='outlined' />
                ))}
              </Box>
            </details>
            <Box>
              <Button
                variant='contained'
                size='small'
                onClick={addMetric}
                disabled={!mLabel.trim() || !formulaCheck?.ok}
              >
                Add metric
              </Button>
            </Box>
            {fields.metrics.map(m => (
              <Box key={m.key} className='flex items-center justify-between rounded border px-3 py-2'>
                <span>
                  <strong>{m.label}</strong> <code className='text-[12px] text-gray-600'>{m.formula}</code>
                </span>
                <IconButton
                  size='small'
                  aria-label={`Remove ${m.label}`}
                  onClick={() => onChange({ ...fields, metrics: fields.metrics.filter(x => x.key !== m.key) })}
                >
                  ✕
                </IconButton>
              </Box>
            ))}
          </Box>
        )}

        {tab === 1 && (
          <Box className='mt-4 flex flex-col gap-3'>
            <Box className='flex gap-2'>
              <TextField
                size='small'
                label='Name'
                placeholder='Offer Type'
                value={dLabel}
                onChange={e => setDLabel(e.target.value)}
                fullWidth
              />
              <Select size='small' value={dField} onChange={e => setDField(e.target.value)}>
                {dimensionOptions.map(d => (
                  <MenuItem key={d.value} value={d.value}>
                    {d.label}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <Typography variant='caption' color='text.secondary'>
              Buckets are checked in order and the first match wins. Values are comma separated.
            </Typography>
            {dBuckets.map((bucket, i) => (
              <Box key={i} className='flex gap-2'>
                <TextField
                  size='small'
                  label='Bucket'
                  value={bucket.label}
                  onChange={e => setBucket(i, { label: e.target.value })}
                />
                <TextField
                  size='small'
                  label='Matches'
                  placeholder='CTV, Online Video'
                  value={bucket.values.join(', ')}
                  onChange={e =>
                    setBucket(i, {
                      values: e.target.value
                        .split(',')
                        .map(v => v.trim())
                        .filter(Boolean),
                    })
                  }
                  fullWidth
                />
                <IconButton
                  size='small'
                  aria-label={`Remove bucket ${i + 1}`}
                  onClick={() => removeBucket(i)}
                  disabled={dBuckets.length === 1}
                >
                  ✕
                </IconButton>
              </Box>
            ))}
            <Box className='flex gap-2'>
              <Button size='small' onClick={() => setDBuckets(prev => [...prev, { label: '', values: [] }])}>
                Add bucket
              </Button>
              <TextField size='small' label='Fallback' value={dFallback} onChange={e => setDFallback(e.target.value)} />
              <Button variant='contained' size='small' onClick={addDimension} disabled={!canAddDimension}>
                Add dimension
              </Button>
            </Box>
            {fields.dimensions.map(d => (
              <Box key={d.key} className='flex items-center justify-between rounded border px-3 py-2'>
                <span>
                  <strong>{d.label}</strong>{' '}
                  <span className='text-[12px] text-gray-600'>
                    over {d.field} · {d.buckets.length} buckets → {d.fallback}
                  </span>
                </span>
                <IconButton
                  size='small'
                  aria-label={`Remove ${d.label}`}
                  onClick={() => onChange({ ...fields, dimensions: fields.dimensions.filter(x => x.key !== d.key) })}
                >
                  ✕
                </IconButton>
              </Box>
            ))}
          </Box>
        )}

        {tab === 2 && (
          <Box className='mt-4 flex flex-col gap-2'>
            <Typography variant='caption' color='text.secondary'>
              Rename a field for this dashboard. Blank restores the standard label.
            </Typography>
            {dimensionOptions.map(d => (
              <TextField
                key={d.value}
                size='small'
                label={d.label}
                value={fields.labels[d.value] ?? ''}
                placeholder={d.label}
                onChange={e => {
                  const labels = { ...fields.labels };
                  if (e.target.value.trim()) labels[d.value] = e.target.value;
                  else delete labels[d.value];
                  onChange({ ...fields, labels });
                }}
              />
            ))}
          </Box>
        )}

        {tab === 3 && (
          <Box className='mt-4 flex flex-col'>
            <Typography variant='caption' color='text.secondary'>
              Hidden metrics are removed from pickers on this dashboard.
            </Typography>
            {CANONICAL_METRICS.filter(m => allowedMetricKeys.includes(m.key)).map(m => (
              <FormControlLabel
                key={m.key}
                control={
                  <Checkbox
                    size='small'
                    checked={!fields.hiddenMetrics.includes(m.key)}
                    onChange={e =>
                      onChange({
                        ...fields,
                        hiddenMetrics: e.target.checked
                          ? fields.hiddenMetrics.filter(k => k !== m.key)
                          : [...fields.hiddenMetrics, m.key],
                      })
                    }
                  />
                }
                label={m.label}
              />
            ))}
          </Box>
        )}

        <Box className='mt-5 flex justify-end'>
          <Button variant='outlined' color='inherit' onClick={onClose}>
            Done
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default TenantFieldsDialog;
